import { useCallback, useEffect, useState } from 'react';
import { HttpMethod, HttpWrapper } from '../../../../helpers/httpWrapper';
import {
	IWebhookCatalogResponse, IWebhookCreateRequest, IWebhookCreateResponse, IWebhookDeliveriesRequest, IWebhookDeliveriesResponse,
	IWebhookDeliveryDetailResponse, IWebhookEndpoint, IWebhookIdRequest, IWebhookListResponse, IWebhookResendRequest, IWebhookResendResponse,
	IWebhookRollSecretResponse, IWebhookTestSendRequest, IWebhookTestSendResponse, IWebhookUpdateRequest, VPlatformWebhookCommunity,
	VPlatformWebhookCreate, VPlatformWebhookDeliveries, VPlatformWebhookId, VPlatformWebhookResend, VPlatformWebhookTestSend,
	VPlatformWebhookUpdate, WithCommunityId,
} from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';
import { IVerifyWebhookUrlRequest, IVerifyWebhookUrlResponse, VVerifyWebhookUrl } from '../../../../networked-shared-interfaces/interfaces-phase2/platform-admin/sub-routes/webhook-subscriptions';
import { URLWrapper } from '../../../../networked-shared-interfaces/url-wrapper';
import { toastService } from '../../../../services/toast.service';

const urls = URLWrapper.urls.platformAdmin.externalVendor.webhooks;
export const WEBHOOK_TEST_MAX_POLLS = 30;

export const useWebhooks = (communityId: string) => {
	const [list, setList] = useState<IWebhookListResponse | null>(null);
	const [catalog, setCatalog] = useState<IWebhookCatalogResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [busyId, setBusyId] = useState<string | null>(null);

	const fail = (fallback: string) => (err: any) => {
		toastService.error(err?.message || fallback);
		return undefined;
	};

	const refresh = useCallback(async () => {
		if (!communityId) return;
		setLoading(true);
		try {
			const q = { communityId };
			const [l, c] = await Promise.all([
				HttpWrapper.callUrl<typeof q, IWebhookListResponse>(urls.list, HttpMethod.GET, q, VPlatformWebhookCommunity),
				HttpWrapper.callUrl<typeof q, IWebhookCatalogResponse>(urls.catalog, HttpMethod.GET, q, VPlatformWebhookCommunity),
			]);
			setList(l);
			setCatalog(c);
		} catch (err) {
			fail('Could not load webhook endpoints')(err);
		} finally {
			setLoading(false);
		}
	}, [communityId]);

	useEffect(() => {
		setList(null);
		setCatalog(null);
		refresh();
	}, [refresh]);

	/** Runs a write, then reloads the list. Resolves undefined on failure (toast shown). */
	const write = async <T,>(fn: () => Promise<T>, errMsg: string, setBusy: (b: boolean) => void = setSaving): Promise<T | undefined> => {
		setBusy(true);
		try {
			const res = await fn();
			await refresh();
			return res;
		} catch (err) {
			return fail(errMsg)(err);
		} finally {
			setBusy(false);
		}
	};

	const create = (payload: IWebhookCreateRequest) =>
		write(
			() => HttpWrapper.callUrl<WithCommunityId<IWebhookCreateRequest>, IWebhookCreateResponse>(urls.create, HttpMethod.POST, { ...payload, communityId }, VPlatformWebhookCreate),
			'Failed to save webhook endpoint'
		);

	const update = (payload: IWebhookUpdateRequest, perRow = false) =>
		write(
			() => HttpWrapper.callUrl<WithCommunityId<IWebhookUpdateRequest>, IWebhookEndpoint>(urls.update, HttpMethod.PUT, { ...payload, communityId }, VPlatformWebhookUpdate),
			'Failed to save webhook endpoint',
			perRow ? (b) => setBusyId(b ? payload.id : null) : setSaving
		);

	const remove = (id: string) =>
		write(
			() => HttpWrapper.callUrl<WithCommunityId<IWebhookIdRequest>, {}>(urls.delete, HttpMethod.DELETE, { id, communityId }, VPlatformWebhookId),
			'Failed to delete webhook endpoint',
			(b) => setBusyId(b ? id : null)
		);

	const rollSecret = async (id: string) => {
		try {
			return await HttpWrapper.callUrl<WithCommunityId<IWebhookIdRequest>, IWebhookRollSecretResponse>(urls.rollSecret, HttpMethod.POST, { id, communityId }, VPlatformWebhookId);
		} catch (err) {
			return fail('Failed to roll the secret')(err);
		}
	};

	const testSend = async (id: string, event: string) => {
		try {
			return await HttpWrapper.callUrl<WithCommunityId<IWebhookTestSendRequest>, IWebhookTestSendResponse>(urls.testSend, HttpMethod.POST, { id, event, communityId }, VPlatformWebhookTestSend);
		} catch (err) {
			return fail('Test send failed')(err);
		}
	};

	const deliveries = (q: IWebhookDeliveriesRequest) =>
		HttpWrapper.callUrl<WithCommunityId<IWebhookDeliveriesRequest>, IWebhookDeliveriesResponse>(urls.deliveries, HttpMethod.GET, { ...q, communityId }, VPlatformWebhookDeliveries);

	/** Throws on failure so the poller / viewer can decide how to react. */
	const delivery = (id: string) =>
		HttpWrapper.callUrl<WithCommunityId<IWebhookIdRequest>, IWebhookDeliveryDetailResponse>(urls.delivery, HttpMethod.GET, { id, communityId }, VPlatformWebhookId);

	const resend = async (body: IWebhookResendRequest) => {
		try {
			return await HttpWrapper.callUrl<WithCommunityId<IWebhookResendRequest>, IWebhookResendResponse>(urls.resend, HttpMethod.POST, { ...body, communityId }, VPlatformWebhookResend);
		} catch (err) {
			return fail('Resend failed')(err);
		}
	};

	/** Stateless probe of the URL (same platform route the legacy card used). */
	const verifyUrl = async (url: string) => {
		try {
			return await HttpWrapper.callUrl<IVerifyWebhookUrlRequest, IVerifyWebhookUrlResponse>(
				URLWrapper.urls.platformAdmin.externalVendor.verifyWebhookUrl, HttpMethod.POST, { url }, VVerifyWebhookUrl
			);
		} catch (err) {
			return fail('Failed to verify webhook URL')(err);
		}
	};

	return { list, catalog, loading, saving, busyId, refresh, create, update, remove, rollSecret, testSend, deliveries, delivery, resend, verifyUrl };
};

export type WebhooksApi = ReturnType<typeof useWebhooks>;
